/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useOrderDetails } from "./saloon-order-display/hooks/useOrderDetails";
import OrderHeader from "./saloon-order-display/components/OrderHeader";
import OrderSummary from "./saloon-order-display/components/OrderSummary";
import OrderItemsTable from "./saloon-order-display/components/OrderItemsTable";
import PaymentDialog from "./saloon-order-display/components/PaymentDialog";
import CancelOrderDialog from "./saloon-order-display/components/CancelDialog";
import { useCancelHandler } from "./saloon-order-display/hooks/useCancelHandler";
import { usePaymentHandler } from "./saloon-order-display/hooks/usePaymentHandler";
import OrderActions from "./saloon-order-display/components/OrderActions";
import { PrintData } from "src/print-configuration/PrintConfigration";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import LoadingScreen from "src/shared/components/LoadingScreen";

const SaloonOrderDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { orderid } = useParams();
  const { order, outstandingAmount, fetchOrderDetails, updateOrderStatus } = useOrderDetails(orderid);
  const [printOpen, setPrintOpen] = useState<boolean>(false);
  const { paymentData, openPaymentDialog, setOpenPaymentDialog, isProcessing, handlePaymentInputChange, processPayment, resetPaymentData, openPaymentWithMethod } = usePaymentHandler( orderid, order, outstandingAmount, fetchOrderDetails );
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails?.[0]?.orderedOn?.$date ?? "");
  const { cancelData, openCancelDialog, setOpenCancelDialog, handleCancelOrder, setCancelData } = useCancelHandler(updateOrderStatus);
  const ordersList = order?.orders || [];
  const firstOrder = ordersList[0];
  const lastOrder = ordersList[ordersList.length - 1];
  const paymentHistory = order?.paymentHistory || [];

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ");

  const { sidebarThemeClass } = useUserTheme();

  const formattedProducts =
    order?.orderDetails?.[0]?.products?.map((p: any) => ({
      productName: p.productName || p.name || "",
      arabicword: p.arabicName || p.arabicword || "",
      amount: Number(p.unitPrice || p.price || p.amount || 0),
      quantity: Number(p.quantity || 1),
      totalPrice: Number(p.lineTotal ?? p.itemTotal ?? Number(p.unitPrice || p.price || 0) * Number(p.quantity || 1)),
    })) || [];

  const latestPayment = paymentHistory.length > 0 ? paymentHistory[paymentHistory.length - 1] : null;

  const paid = latestPayment ? Number(latestPayment.receivedAmount || 0) : lastOrder?.receivedAmount !== undefined ? Number(lastOrder.receivedAmount) : 0;

  const prevPaid = paymentHistory.length > 1 ? paymentHistory.slice(0, -1).reduce((sum, item) => sum + Number(item?.receivedAmount || 0), 0) : 0;

  const totalPaidSoFar = paymentHistory.reduce((sum, item) => sum + Number(item?.receivedAmount || 0), 0);
  const orderGrossTotal = Number(firstOrder?.orderAmount ?? 0);
  const orderNetTotal = Number(firstOrder?.finalAmount ?? orderGrossTotal);
  const remainingAmountValue = lastOrder?.remainingAmount !== undefined ? Number(lastOrder.remainingAmount) : Math.max(0, orderNetTotal - totalPaidSoFar);
  const prevBalance = Math.max(0, orderNetTotal - prevPaid);

  const printData: PrintData = {
    CustomerName: customerName || "",
    CustomerPhon: order?.customerDetails?.phones?.[0]?.phone || "",
    OrderId: firstOrder?.orderId ?? lastOrder?.orderId ?? "",
    OrderStatus: lastOrder?.status || firstOrder?.status || "",
    discount: Number(firstOrder?.discount || 0),
    Date: orderDate || "",
    Time: orderTime || "",
    Products: formattedProducts,
    Paid: paid,
    prvPaid: prevPaid,
    prvBalance: prevBalance,
    unpaid: remainingAmountValue,
    OSB: Number(order?.totalOutstandingBalance ?? lastOrder?.outstandingBalance ?? outstandingAmount ?? 0),
    paymentMethod: latestPayment?.paymentMethod || "",
    total: orderGrossTotal,
    subtotal: orderNetTotal,
    balanceReturned: Number(lastOrder?.balanceReturned || 0),
  };

  const handleGoBack = useCallback(() => {
    setOpenPaymentDialog(false);
    setOpenCancelDialog(false);
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    document.title = "Order › Details › iMetanic";
  }, []);

  if (!order) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col overflow-hidden w-full">
      <div className="w-full h-full overflow-hidden">
        <div className="thin-scrollbar w-full flex-1 flex flex-col lg:flex-row gap-2 p-1 h-full overflow-auto">
          {/* Left Column - Order Items (Fixed, no scroll) */}
          <div className={`lg:w-4/6 w-full flex flex-col overflow-hidden shadow-inner rounded-lg ${sidebarThemeClass} p-1`}>
            <OrderHeader order={order} outstandingAmount={outstandingAmount} onPrint={() => setPrintOpen(!printOpen)} onGoBack={handleGoBack} printData={printData} />
            <div className="flex-1 pt-1 min-h-0 overflow-hidden">
              <OrderItemsTable order={order} />
            </div>
          </div>

          {/* Right Column - Order Summary & Actions (Scrollable) */}
          <div className={`thin-scrollbar xl:w-4/12 lg:w-5/12 w-full h-full pr-1 flex-shrink-0 shadow-inner rounded-lg ${sidebarThemeClass} p-1`}>
            {/* ✅ Only this inner div scrolls */}
            <div className="flex-1 overflow-y-auto thin-scrollbar space-y-2">
              <OrderSummary order={order} />
              <OrderActions order={order} onUpdateStatus={updateOrderStatus} onOpenPayment={openPaymentWithMethod} onOpenCancel={() => setOpenCancelDialog(true)} />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <PaymentDialog
        open={openPaymentDialog}
        onClose={resetPaymentData}
        order={order}
        paymentData={paymentData}
        onPaymentDataChange={handlePaymentInputChange}
        onProcessPayment={processPayment}
        isProcessing={isProcessing}
      />

      {/* Cancel Order Dialog */}
      <CancelOrderDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        order={order}
        outstandingAmount={outstandingAmount}
        cancelData={cancelData}
        onCancelDataChange={setCancelData}
        onConfirmCancel={handleCancelOrder}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SaloonOrderDisplay;
